import React from 'react';
import { useTranslation } from 'react-i18next';
import AssetDetailSection from './AssetDetailSection';
import DetailField from './DetailField';
import InputField from '../../../components/ui/InputField';
import type { ObjectAttributesForm } from '../AssetDetailPage';

interface ObjectIdentitySectionProps {
    mode: 'view' | 'edit';
    /** Stored values, for view mode. */
    serialNumber?: string;
    volumeCm3?: number;
    note?: string;
    /** Form state, for edit mode. */
    draft?: ObjectAttributesForm;
    onChange?: (updates: Partial<ObjectAttributesForm>) => void;
}

/**
 * The per-unit facts that identify one physical object: its serial, its exact
 * volume, and a unit-specific quirk note.
 *
 * These are the object-level homes from the field review — an equipment
 * make/model and its manual belong to the Model, shared across every unit and
 * every Product that uses it, while a quirk ("this drill's chuck sticks")
 * belongs to the individual object and is never duplicated onto the Product.
 *
 * Saved through `PATCH /admin/inventory/objects/:assetId`, which **merges**
 * into `accomodationdetail` rather than replacing it — the same column carries
 * the accommodation fields, and `guests` in particular drives the capacity
 * filter. The asset PUT replaces that column wholesale, which is why the page's
 * form preserves keys it does not model.
 *
 * `model` is not here: `asset.model_id` arrives with the equipment catalogue,
 * and there is still no Model registry to point at.
 */
const ObjectIdentitySection: React.FC<ObjectIdentitySectionProps> = ({
    mode,
    serialNumber,
    volumeCm3,
    note,
    draft,
    onChange,
}) => {
    const { t } = useTranslation();

    if (mode === 'view') {
        return (
            <AssetDetailSection title={t('asset_detail.sections.object_identity')}>
                <div className={'flex flex-col gap-4'}>
                    <DetailField
                        label={t('asset_detail.fields.serial_number')}
                        value={serialNumber || undefined}
                        emptyLabel={'na'}
                    />
                    <DetailField
                        label={t('asset_detail.fields.volume_cm3')}
                        value={volumeCm3 != null ? String(volumeCm3) : undefined}
                        emptyLabel={'na'}
                    />
                    <DetailField
                        label={t('asset_detail.fields.object_note')}
                        value={note || undefined}
                        emptyLabel={'na'}
                    />
                </div>
            </AssetDetailSection>
        );
    }

    return (
        <AssetDetailSection title={t('asset_detail.sections.object_identity')}>
            <div className={'flex flex-col gap-4'}>
                <InputField
                    label={t('asset_detail.fields.serial_number')}
                    value={draft?.serialNumber ?? ''}
                    onChange={(value) => onChange?.({ serialNumber: value })}
                    labelAbove={true}
                />
                <InputField
                    label={t('asset_detail.fields.volume_cm3')}
                    value={draft?.volumeCm3 ?? ''}
                    onChange={(value) => onChange?.({ volumeCm3: value })}
                    type={'number'}
                    integerOnly={true}
                    min={0}
                    labelAbove={true}
                />
                <InputField
                    label={t('asset_detail.fields.object_note')}
                    value={draft?.note ?? ''}
                    onChange={(value) => onChange?.({ note: value })}
                    labelAbove={true}
                />
            </div>
        </AssetDetailSection>
    );
};

export default ObjectIdentitySection;
